import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  Checkbox,
  Avatar,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import type { Settings } from '../../types/settings';
import { getExchangeLogo } from '../../utils/exchangeUrls';
import { EXCHANGES } from '../../types/opportunity';

interface ExchangeTogglesV2Props {
  buyExchanges: Settings['buyExchanges'];
  sellExchanges: Settings['sellExchanges'];
  onBuyChange: (exchanges: Settings['buyExchanges']) => void;
  onSellChange: (exchanges: Settings['sellExchanges']) => void;
}

const ExchangeTogglesV2: React.FC<ExchangeTogglesV2Props> = ({
  buyExchanges,
  sellExchanges,
  onBuyChange,
  onSellChange,
}) => {
  const { t } = useTranslation();
  const handleBuyToggle = (exchangeIndex: number) => {
    // If undefined, treat as enabled (true) by default
    const currentValue = buyExchanges[exchangeIndex] !== false;
    const newValue = !currentValue;
    onBuyChange({
      ...buyExchanges,
      [exchangeIndex]: newValue
    });
  };

  const handleSellToggle = (exchangeIndex: number) => {
    // If undefined, treat as enabled (true) by default  
    const currentValue = sellExchanges[exchangeIndex] !== false;
    const newValue = !currentValue;
    onSellChange({
      ...sellExchanges,
      [exchangeIndex]: newValue
    });
  };

  const ExchangeToggleRow = ({ 
    exchangeName, 
    index, 
    buyEnabled, 
    sellEnabled 
  }: { 
    exchangeName: string; 
    index: number; 
    buyEnabled: boolean; 
    sellEnabled: boolean;
  }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2, 
      py: 1,
      '&:hover': {
        bgcolor: 'action.hover',
      }
    }}>
      {/* Exchange Logo and Name */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        minWidth: '140px',
        flex: '0 0 auto'
      }}>
        <Avatar
          src={getExchangeLogo(index)}
          alt={exchangeName}
          sx={{
            width: 24,
            height: 24,
          }}
        />
        <Typography variant="body2">
          {exchangeName}
        </Typography>
      </Box>
      
      {/* Buy Checkbox */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <Checkbox
          checked={buyEnabled}
          onChange={() => handleBuyToggle(index)}
          color="primary"
          size="small"
          sx={{
            '& .MuiSvgIcon-root': { 
              fontSize: 20,
              color: buyEnabled ? 'primary.main' : 'text.disabled'
            }
          }}
        />
      </Box>
      
      {/* Sell Checkbox */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        <Checkbox
          checked={sellEnabled}
          onChange={() => handleSellToggle(index)}
          color="secondary"
          size="small"
          sx={{
            '& .MuiSvgIcon-root': { 
              fontSize: 20,
              color: sellEnabled ? 'secondary.main' : 'text.disabled'
            }
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
        {t('settings.exchanges.description')}
      </Typography>
      
      <Paper variant="outlined" sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 1
        }}>
          <Box sx={{ minWidth: '140px', flex: '0 0 auto' }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              {t('settings.exchanges.exchange')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" fontWeight="bold" color="primary.main">
                {t('settings.exchanges.fromBuy')}
              </Typography>
              <Checkbox
                size="small"
                checked={Object.values(buyExchanges).every(v => v !== false)}
                indeterminate={
                  Object.values(buyExchanges).some(v => v !== false) && 
                  !Object.values(buyExchanges).every(v => v !== false)
                }
                onChange={(e) => {
                  const newState: Settings['buyExchanges'] = {};
                  EXCHANGES.forEach((_, i) => { newState[i] = e.target.checked; });
                  onBuyChange(newState);
                }}
                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" fontWeight="bold" color="secondary.main">
                {t('settings.exchanges.toSell')}
              </Typography>
              <Checkbox
                size="small"
                checked={Object.values(sellExchanges).every(v => v !== false)}
                indeterminate={
                  Object.values(sellExchanges).some(v => v !== false) && 
                  !Object.values(sellExchanges).every(v => v !== false)
                }
                onChange={(e) => {
                  const newState: Settings['sellExchanges'] = {};
                  EXCHANGES.forEach((_, i) => { newState[i] = e.target.checked; });
                  onSellChange(newState);
                }}
                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}
              />
            </Box>
          </Box>
        </Box>
        
        {/* Exchange rows */}
        <Box>
          {EXCHANGES.map((exchangeName, index) => {
            const buyEnabled = buyExchanges[index] ?? true;
            const sellEnabled = sellExchanges[index] ?? true;
            return (
              <ExchangeToggleRow
                key={index}
                exchangeName={exchangeName}
                index={index}
                buyEnabled={buyEnabled}
                sellEnabled={sellEnabled}
              />
            );
          })}
        </Box>
        
      </Paper>
    </Box>
  );
};

export default ExchangeTogglesV2;